import { ReactNode, useCallback } from "react";
import { Box, Text } from "@chakra-ui/react";
import { WfLinkGhost } from "src/components/base/wf-link";
import { LinkWithIconTruncatable } from "src/components/common/link-with-icon";
import { IFeedCardLinkBody } from "src/components/feed-card/types/content";
import { EnumFeedCardLinkVariant } from "src/components/feed-card/types/enums";
import { IconUnderlying, IconWikifolio, TIconComponent } from "src/components/icons";
import { triggerCallbackOnMiddleClickEvent } from "src/utils/dom/mouse-util";
import { isUrlPathnameSameAsCurrentPage } from "../../utils/link-helper";

const BLANK = "_blank";

export interface IFeedCardLinkProps extends IFeedCardLinkBody {
    onClick?: (url: string) => void;
}

export const FeedCardLink = ({ variant, url, name, onClick }: IFeedCardLinkProps) => {
    const handleLinkClick = useCallback(() => onClick?.(url), [url, onClick]);

    switch (variant) {
        case EnumFeedCardLinkVariant.Underlying:
            return DefaultLinkVariant({ url, onClick: handleLinkClick, name, icon: IconUnderlying });
        case EnumFeedCardLinkVariant.Wikifolio:
            return DefaultLinkVariant({ url, onClick: handleLinkClick, name, icon: IconWikifolio });
        case EnumFeedCardLinkVariant.Plain:
            return (
                <LinkTargetBlank url={url} onClick={handleLinkClick}>
                    {name}
                </LinkTargetBlank>
            );
        default:
            return (
                <UnsupportedVariant variant={variant} url={url} onClick={handleLinkClick}>
                    {name}
                </UnsupportedVariant>
            );
    }
};

interface ILinkTargetBlank {
    url: string;
    children: ReactNode;
    onClick?: () => void;
}

interface ILinkVariant {
    url: string;
    name: string;
    icon: TIconComponent;
    onClick?: () => void;
}

const LinkTargetBlank = ({ url, children, onClick }: ILinkTargetBlank) => (
    <WfLinkGhost href={url} target={BLANK} onClick={onClick} onAuxClick={triggerCallbackOnMiddleClickEvent(onClick)}>
        {children}
    </WfLinkGhost>
);

const UnsupportedVariant = ({ variant, children, ...restProps }: { variant: never } & ILinkTargetBlank) => (
    <LinkTargetBlank {...restProps}>{children}</LinkTargetBlank>
);

const DefaultLinkVariant = ({ url, onClick, name, icon: IconComponent }: ILinkVariant) => {
    if (isUrlPathnameSameAsCurrentPage(url)) {
        return (
            <Box verticalAlign="bottom" maxW="100%" alignItems="center" display="inline-flex">
                <IconComponent />
                <Text as="span" ml={0.5} verticalAlign="middle" display="block" noOfLines={1} wordBreak="break-word">
                    {name}
                </Text>
            </Box>
        );
    }

    return (
        <LinkWithIconTruncatable
            icon={IconComponent}
            url={url}
            linkProps={{ onClick: onClick, onAuxClick: triggerCallbackOnMiddleClickEvent(onClick), target: BLANK }}
        >
            {name}
        </LinkWithIconTruncatable>
    );
};
